import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { X, ShoppingBag } from "lucide-react";
import { NotifyForm } from "@/components/merch/NotifyForm";
import { STORE_OPEN, fmt } from "@/lib/storeConfig";

export const ProductModal = ({ product, onClose, onAdd }) => {
  const oneSize = product.sizes.length === 1;
  const [size, setSize] = useState(oneSize ? product.sizes[0] : null);
  const [view, setView] = useState("front");
  const imgSrc = view === "back" && product.img_back ? product.img_back : product.img;
  const label = useMemo(
    () => [product.drop, product.category, product.color].filter((v, i, a) => a.findIndex((x) => x.toLowerCase() === v.toLowerCase()) === i).join(" · "),
    [product.drop, product.category, product.color]
  );

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-md"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-[80] flex items-center justify-center px-4 pointer-events-none">
        <motion.div
          data-testid="merch-product-modal"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.3 }}
          className="relative w-full max-w-3xl bg-[#0a0a0a] border border-[#D4AF37]/30 max-h-[90vh] overflow-y-auto pointer-events-auto"
        >
          <button data-testid="merch-modal-close" onClick={onClose} className="absolute top-4 right-4 z-10 text-white/60 hover:text-white bg-black/50 p-2">
            <X size={20} />
          </button>
          <div className="grid sm:grid-cols-2">
            <div className="relative aspect-square bg-black">
              <img src={imgSrc} alt={`${product.name} — ${view}`} className="w-full h-full object-cover" />
              {product.img_back && (
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                  {["front", "back"].map((v) => (
                    <button
                      key={v}
                      data-testid={`merch-view-${v}`}
                      onClick={() => setView(v)}
                      className={`text-[10px] uppercase tracking-[0.25em] px-4 py-1.5 border backdrop-blur-sm transition-all ${
                        view === v
                          ? "border-[#D4AF37] text-black bg-[#D4AF37]"
                          : "border-white/30 text-white bg-black/50 hover:border-[#D4AF37]"
                      }`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="p-6 sm:p-8 flex flex-col">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">{label}</p>
              <h2 className="font-display wolf-gold-foil text-3xl sm:text-4xl mt-3 leading-tight">{product.name}</h2>
              <p className="text-sm text-white/60 mt-3">{product.tagline}</p>
              {product.back_print && (
                <p className="font-editorial italic text-lg text-white/70 mt-4 border-l-2 border-[#D4AF37]/50 pl-4">
                  Back print — "{product.back_print}"
                </p>
              )}
              <p className="font-display text-2xl text-white mt-6" data-testid="merch-modal-price">{fmt(product.price)}</p>

              {STORE_OPEN && !oneSize && (
                <div className="mt-6">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-3">Select size</p>
                  <div className="flex gap-2 flex-wrap">
                    {product.sizes.map((s) => (
                      <button
                        key={s}
                        data-testid={`merch-size-${s.toLowerCase().replace(/[^a-z0-9]/g, "")}`}
                        onClick={() => setSize(s)}
                        className={`min-w-[46px] px-3 py-2.5 text-sm border transition-all ${
                          size === s
                            ? "border-[#D4AF37] text-[#D4AF37] bg-[#7A5A18]/20"
                            : "border-white/15 text-white/60 hover:border-white/40 hover:text-white"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {STORE_OPEN ? (
                <button
                  data-testid="merch-add-to-cart"
                  disabled={!size}
                  onClick={() => onAdd(product, size)}
                  className="mt-8 w-full inline-flex items-center justify-center gap-2 text-[12px] uppercase tracking-[0.3em] font-semibold text-black bg-[#D4AF37] hover:bg-[#F4C542] disabled:opacity-40 disabled:cursor-not-allowed px-6 py-4 transition-all"
                >
                  <ShoppingBag size={16} />
                  {size ? "Add to Cart" : "Choose a size"}
                </button>
              ) : (
                <>
                  <div
                    data-testid="merch-modal-coming-soon"
                    className="mt-8 w-full text-center text-[12px] uppercase tracking-[0.3em] font-semibold text-[#D4AF37] border border-[#D4AF37]/50 bg-[#7A5A18]/15 px-6 py-4"
                  >
                    Coming Soon
                  </div>
                  <p className="text-[10px] uppercase tracking-[0.28em] text-white/45 mt-5 mb-3">Get first access when it drops</p>
                  <NotifyForm testIdPrefix="merch-modal-notify" productId={product.id} />
                </>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
};
